/*
    Login Page of the Website, uses signIn function from aws-amplify/auth
    Saves user information in localstorage - userData
    Includes inline forgot-password flow (Cognito resetPassword + confirmResetPassword)
*/

import React, { useState, useEffect } from 'react';
import '../App.css';
import TextField from '@mui/material/TextField';
import { FormControl, IconButton, InputAdornment, Typography, Alert } from '@mui/material';
import CustomButton from '../components/Button';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { signIn, confirmSignIn, fetchUserAttributes, getCurrentUser, resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { useNavigate } from 'react-router-dom'; 
import { signOut } from 'aws-amplify/auth';

function Login() {
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [userAttributes, setUserAttributes] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate(); 

    // Forgot-password state
    const [forgotMode, setForgotMode] = useState(false);       // show forgot-password form
    const [resetStep, setResetStep] = useState('request');      // 'request' | 'confirm'
    const [resetEmail, setResetEmail] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [resetError, setResetError] = useState('');

    // First-login state. An admin-created (invited) user arrives in Cognito
    // FORCE_CHANGE_PASSWORD: signIn() succeeds but returns a
    // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED next step instead of a session.
    // We used to ignore nextStep and call fetchUserAttributes() straight away,
    // which threw "User needs to be authenticated to call this API." and made
    // every invited account unusable (2026-07-27).
    const [newPwMode, setNewPwMode] = useState(false);
    const [firstPassword, setFirstPassword] = useState('');
    const [firstPasswordConfirm, setFirstPasswordConfirm] = useState('');

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    async function handleSubmit(event) {
        event.preventDefault();
        setError('');
        await signOut({ global: true });
        localStorage.removeItem('selectedDevice');
        try {
            const { isSignedIn, nextStep } = await signIn({
                username: email,
                password: password,
            });
            // Invited users must set a password before they have a session.
            if (!isSignedIn && nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                setNewPwMode(true);
                return;
            }
            if (!isSignedIn) {
                // Any other challenge (MFA, etc.) is not implemented here; say so
                // rather than failing with a confusing "needs to be authenticated".
                setError(`Additional sign-in step required: ${nextStep?.signInStep || 'unknown'}. Contact support.`);
                return;
            }
            await completeSignIn();
        } catch (err) {
            await signOut({ global: true });
            console.error('Sign-in error:', err);
            setError(err.message || 'Sign-in failed. Please check your credentials.');
        }
    }

    /** Load attributes for an established session and enter the app. */
    async function completeSignIn() {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
        localStorage.setItem('userData', JSON.stringify(attributes));
        navigate('/dashboard');
    }

    /** Finish the first-login challenge by setting a permanent password. */
    async function handleSetFirstPassword() {
        setError('');
        if (firstPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (firstPassword !== firstPasswordConfirm) {
            setError('Passwords do not match.');
            return;
        }
        try {
            const { isSignedIn } = await confirmSignIn({ challengeResponse: firstPassword });
            if (!isSignedIn) {
                setError('Could not complete sign-in. Please try again.');
                return;
            }
            setNewPwMode(false);
            setFirstPassword('');
            setFirstPasswordConfirm('');
            await completeSignIn();
        } catch (err) {
            console.error('Set-password error:', err);
            setError(err.message || 'Could not set the new password.');
        }
    }
  
    async function getUser() { 
        try {
            if (localStorage.getItem('userData')) {
                const user = await getCurrentUser();
                if (user) {
                    const attributes = await fetchUserAttributes(user);
                    setUserAttributes(attributes);
                    navigate('/dashboard');
                }
            }
        } catch (err) {
            await signOut({ global: true });
            localStorage.removeItem('userData');
            navigate('/');
        }
    }
  
    useEffect(() => {
        getUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Forgot Password handlers ---
    async function handleResetRequest(e) {
        e.preventDefault();
        setResetError('');
        setResetMessage('');
        try {
            await resetPassword({ username: resetEmail });
            setResetMessage('Verification code sent to your email.');
            setResetStep('confirm');
        } catch (err) {
            console.error('Reset password error:', err);
            setResetError(err.message || 'Failed to send reset code.');
        }
    }

    async function handleResetConfirm(e) {
        e.preventDefault();
        setResetError('');
        setResetMessage('');
        try {
            await confirmResetPassword({
                username: resetEmail,
                confirmationCode,
                newPassword,
            });
            setResetMessage('Password reset successful! You can now log in.');
            // Return to login after short delay
            setTimeout(() => {
                setForgotMode(false);
                setResetStep('request');
                setResetEmail('');
                setConfirmationCode('');
                setNewPassword('');
                setResetMessage('');
            }, 2000);
        } catch (err) {
            console.error('Confirm reset error:', err);
            setResetError(err.message || 'Failed to reset password.');
        }
    }

    function handleBackToLogin() {
        setForgotMode(false);
        setResetStep('request');
        setResetEmail('');
        setConfirmationCode('');
        setNewPassword('');
        setResetMessage('');
        setResetError('');
    }

    const textFieldSx = {
        marginBottom: 2,
        backgroundColor: 'white',
        '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
        borderRadius: '5px'
    };

    // --- First-login: set a permanent password (invited users) ---
    if (newPwMode) {
        return (
            <div className="container">
            <div className="LoginDiv">
                <FormControl fullWidth>
                <h2>Set Your Password</h2>
                <Typography sx={{ color: 'white', mb: 2 }}>
                    This is your first sign-in. Choose a password to finish setting up
                    your account.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={firstPassword}
                    onChange={(e) => setFirstPassword(e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    label="Confirm New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={firstPasswordConfirm}
                    onChange={(e) => setFirstPasswordConfirm(e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                />
                <CustomButton onClick={handleSetFirstPassword} text="Set Password & Continue" />
                </FormControl>
            </div>
            </div>
        );
    }

    // --- Forgot Password UI ---
    if (forgotMode) {
        return (
            <div className="container">
            <div className="LoginDiv">
                <FormControl fullWidth>
                <h2>Reset Password</h2>

                {resetMessage && <Alert severity="success" sx={{ mb: 2 }}>{resetMessage}</Alert>}
                {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}

                {resetStep === 'request' && (
                    <>
                    <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
                        Enter your email address and we'll send you a verification code.
                    </Typography>
                    <TextField
                        type="text"
                        variant="outlined"
                        placeholder="Email ID"
                        fullWidth
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        sx={textFieldSx}
                    />
                    <CustomButton onClick={handleResetRequest} text="Send Code" />
                    </>
                )}

                {resetStep === 'confirm' && (
                    <>
                    <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
                        Enter the verification code sent to {resetEmail} and your new password.
                    </Typography>
                    <TextField
                        type="text"
                        variant="outlined"
                        placeholder="Verification Code"
                        fullWidth
                        value={confirmationCode}
                        onChange={(e) => setConfirmationCode(e.target.value)}
                        sx={textFieldSx}
                    />
                    <TextField
                        type="password"
                        variant="outlined"
                        placeholder="New Password"
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={textFieldSx}
                    />
                    <CustomButton onClick={handleResetConfirm} text="Reset Password" />
                    </>
                )}

                <Typography
                    variant="body2"
                    sx={{ mt: 2, color: '#90caf9', cursor: 'pointer', textAlign: 'center' }}
                    onClick={handleBackToLogin}
                >
                    ← Back to Login
                </Typography>
                </FormControl>
            </div>
            </div>
        );
    }

    // --- Login UI ---
    return (
        <div className="container">
        <div className="LoginDiv">
            <FormControl fullWidth>
            <h2>Log In</h2>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField 
                name="email" 
                type="text" 
                variant="outlined" 
                placeholder="Email ID" 
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}  
                sx={textFieldSx}
            />
            <TextField 
                name="password"
                type={showPassword ? 'text' : 'password'} 
                variant="outlined" 
                placeholder="Password" 
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)} 
                sx={textFieldSx}
                InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                    <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                    >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    </InputAdornment>
                ),
                }}
            />
            <Typography
                variant="body2"
                sx={{ color: '#90caf9', cursor: 'pointer', mb: 1 }}
                onClick={() => setForgotMode(true)}
            >
                Forgot Password?
            </Typography>
            <CustomButton onClick={handleSubmit} text="Log In" sx={{ marginTop: 2 }} />
            </FormControl>
        </div>
        </div>
    );
}

export default Login;

